import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { submitPaper, getPaperForRevision } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const SubmitPaper = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const revisionId = queryParams.get('revision');
  
  // Categories that require ethics statement
  const categoriesRequiringEthics = ['Animal Science', 'Clinical Research', 'Human Studies'];
  
  const [formData, setFormData] = useState({
    title: '',
    abstract: '',
    keywords: '',
    authors: [{ name: '', email: '', affiliation: '', isPrimaryContact: true }],
    manuscriptFile: null,
    coverLetter: '',
    category: '',
    fundingInfo: '',
    ethicsStatement: '',
    conflictOfInterest: '',
    acknowledgments: '',
  });
  
  const [isRevision, setIsRevision] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [apiError, setApiError] = useState(null);
  
  useEffect(() => {
    // Check if this is a revision submission
    if (revisionId) {
      setIsRevision(true);
      loadPaperForRevision(revisionId);
    }
  }, [revisionId]);
  
  const loadPaperForRevision = async (id) => {
    try {
      const paperData = await getPaperForRevision(id);
      
      // Populate form with existing paper data
      setFormData({
        ...paperData,
        manuscriptFile: null, // File needs to be re-uploaded
      });
    } catch (err) {
      console.error('Error loading paper for revision:', err);
      setApiError('Failed to load paper data for revision. Please try again later.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear field-specific error when user makes changes
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
    
    // Clear API error when user makes any change
    if (apiError) {
      setApiError(null);
    }
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setErrors({
          ...errors,
          manuscriptFile: 'File is too large. Maximum size is 10MB.'
        });
        return;
      }
      
      // Check file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        setErrors({
          ...errors,
          manuscriptFile: 'Invalid file type. Please upload a PDF or Word document.'
        });
        return;
      }
      
      setFormData({ ...formData, manuscriptFile: file });
      setErrors({ ...errors, manuscriptFile: null });
    }
  };
  
  const handleAuthorChange = (index, field, value) => {
    const updatedAuthors = [...formData.authors];
    updatedAuthors[index] = { ...updatedAuthors[index], [field]: value };
    setFormData({ ...formData, authors: updatedAuthors });
    
    // Clear author-related errors
    if (errors.authors) {
      setErrors({ ...errors, authors: null });
    }
  };
  
  const addAuthor = () => {
    setFormData({
      ...formData,
      authors: [
        ...formData.authors,
        { name: '', email: '', affiliation: '', isPrimaryContact: false }
      ]
    });
  };
  
  const removeAuthor = (index) => {
    if (formData.authors.length <= 1) {
      return; // Keep at least one author
    }
    
    const updatedAuthors = [...formData.authors];
    updatedAuthors.splice(index, 1);
    
    // Ensure at least one author is the primary contact
    if (!updatedAuthors.some(author => author.isPrimaryContact)) {
      updatedAuthors[0].isPrimaryContact = true;
    }
    
    setFormData({ ...formData, authors: updatedAuthors });
  };
  
  const setPrimaryContact = (index) => {
    const updatedAuthors = formData.authors.map((author, idx) => ({
      ...author,
      isPrimaryContact: idx === index
    }));
    
    setFormData({ ...formData, authors: updatedAuthors });
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Title validation
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    // Abstract validation
    if (!formData.abstract.trim()) {
      newErrors.abstract = 'Abstract is required';
    } else if (formData.abstract.length > 500) {
      newErrors.abstract = 'Abstract must be less than 500 words';
    }
    
    // Keywords validation
    if (!formData.keywords.trim()) {
      newErrors.keywords = 'Keywords are required';
    }
    
    // Category validation
    if (!formData.category) {
      newErrors.category = 'Please select a category';
    }
    
    // Authors validation
    const authorErrors = [];
    let hasValidAuthor = false;
    
    formData.authors.forEach((author, index) => {
      const authorError = {};
      
      if (!author.name.trim()) {
        authorError.name = 'Author name is required';
      }
      
      if (!author.email.trim()) {
        authorError.email = 'Author email is required';
      } else if (!/\S+@\S+\.\S+/.test(author.email)) {
        authorError.email = 'Invalid email format';
      }
      
      if (!author.affiliation.trim()) {
        authorError.affiliation = 'Author affiliation is required';
      }
      
      if (Object.keys(authorError).length === 0) {
        hasValidAuthor = true;
      }
      
      authorErrors[index] = authorError;
    });
    
    if (!hasValidAuthor) {
      newErrors.authors = 'At least one complete author is required';
    }
    
    // Check if we have a valid primary contact author
    if (!formData.authors.some(author => author.isPrimaryContact && author.name && author.email)) {
      newErrors.primaryContact = 'A valid primary contact author is required';
    }
    
    // Manuscript file validation
    if (!formData.manuscriptFile) {
      newErrors.manuscriptFile = 'Please upload your manuscript';
    }
    
    // Cover letter validation
    if (!formData.coverLetter.trim()) {
      newErrors.coverLetter = 'Cover letter is required';
    }
    
    // Ethics statement for certain categories
    const categoriesRequiringEthics = ['Clinical Research', 'Animal Studies', 'Human Subjects Research'];
    if (categoriesRequiringEthics.includes(formData.category) && !formData.ethicsStatement.trim()) {
      newErrors.ethicsStatement = 'Ethics statement is required for this category';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      window.scrollTo(0, 0); // Scroll to top to show errors
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create FormData to handle file upload
      const submissionData = new FormData();
      
      // Add text fields
      submissionData.append('title', formData.title);
      submissionData.append('abstract', formData.abstract);
      submissionData.append('keywords', formData.keywords);
      submissionData.append('category', formData.category);
      submissionData.append('coverLetter', formData.coverLetter);
      submissionData.append('fundingInfo', formData.fundingInfo);
      submissionData.append('ethicsStatement', formData.ethicsStatement);
      submissionData.append('conflictOfInterest', formData.conflictOfInterest);
      submissionData.append('acknowledgments', formData.acknowledgments);
      
      // Add authors as JSON string
      submissionData.append('authors', JSON.stringify(formData.authors));
      
      // Add manuscript file (backend expects field name 'manuscriptFile')
      submissionData.append('manuscriptFile', formData.manuscriptFile);
      
      // Add revision ID if this is a revision
      if (isRevision) {
        submissionData.append('revisionOf', revisionId);
      }
      
      // Submit the paper
      await submitPaper(submissionData);
      
      // Show success message
      setSubmitSuccess(true);
      
      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    } catch (error) {
      let errorMessage = 'Paper submission failed. Please try again.';
      
      if (error.response) {
        if (error.response.data && error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      }
      
      setApiError(errorMessage);
      window.scrollTo(0, 0); // Scroll to top to show error
    } finally {
      setIsSubmitting(false);
    }
  };

  // Categories for the dropdown
  const categories = [
    'Agricultural Economics and Extension',
    'Animal Science and Production',
    'Crop Science and Production',
    'Soil Science and Land Management',
    'Agricultural Engineering and Mechanization',
    'Food Science and Technology',
    'Forestry and Wildlife Management',
    'Fisheries and Aquaculture',
    'Environmental Sciences',
    'Plant and Animal Biotechnology',
    'Agricultural Biodiversity and Conservation',
    'Climate Change and Agriculture',
    'Clinical Research',
    'Animal Studies',
    'Human Subjects Research',
    'Other'
  ];

  if (submitSuccess) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card">
              <div className="card-body text-center py-5">
                <div className="mb-4">
                  <i className="fas fa-check-circle text-success" style={{ fontSize: '4rem' }}></i>
                </div>
                <h2 className="mb-3">Submission Successful!</h2>
                <p className="lead mb-4">
                  Your paper has been successfully submitted for review. You will be redirected to your dashboard shortly.
                </p>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="row mb-4">
        <div className="col-lg-8 mx-auto">
          <h1 className="mb-3">{isRevision ? 'Submit Revised Paper' : 'Submit New Paper'}</h1>
          <p className="lead">
            {isRevision 
              ? 'Please submit your revised manuscript based on the reviewers\' comments.'
              : 'Complete the form below to submit your manuscript for consideration.'}
          </p>
          
          {apiError && (
            <div className="alert alert-danger" role="alert">
              {apiError}
            </div>
          )}
          
          {Object.keys(errors).length > 0 && (
            <div className="alert alert-warning">
              <h5 className="alert-heading">Please correct the following errors:</h5>
              <ul className="mb-0">
                {Object.entries(errors).map(([key, value]) => (
                  <li key={key}>{value}</li>
                ))}
              </ul>
            </div>
          )}
          
          <div className="card">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                {/* Paper Details */}
                <h4 className="mb-3">Paper Details</h4>
                
                <div className="mb-3">
                  <label htmlFor="title" className="form-label">Title *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                  />
                  {errors.title && (
                    <div className="invalid-feedback">{errors.title}</div>
                  )}
                </div>
                
                <div className="mb-3">
                  <label htmlFor="abstract" className="form-label">Abstract *</label>
                  <textarea
                    className={`form-control ${errors.abstract ? 'is-invalid' : ''}`}
                    id="abstract"
                    name="abstract"
                    rows="5"
                    value={formData.abstract}
                    onChange={handleChange}
                  ></textarea>
                  {errors.abstract && (
                    <div className="invalid-feedback">{errors.abstract}</div>
                  )}
                  <div className="form-text">
                    Maximum 500 words. Current count: {formData.abstract.split(/\s+/).filter(Boolean).length} words
                  </div>
                </div>
                
                <div className="mb-3">
                  <label htmlFor="keywords" className="form-label">Keywords *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.keywords ? 'is-invalid' : ''}`}
                    id="keywords"
                    name="keywords"
                    value={formData.keywords}
                    onChange={handleChange}
                    placeholder="Separate keywords with commas"
                  />
                  {errors.keywords && (
                    <div className="invalid-feedback">{errors.keywords}</div>
                  )}
                  <div className="form-text">
                    Provide 4-6 keywords that capture the main topics of your paper
                  </div>
                </div>
                
                <div className="mb-4">
                  <label htmlFor="category" className="form-label">Category *</label>
                  <select
                    className={`form-select ${errors.category ? 'is-invalid' : ''}`}
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                  >
                    <option value="">Select a category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                  {errors.category && (
                    <div className="invalid-feedback">{errors.category}</div>
                  )}
                </div>
                
                {/* Authors Section */}
                <h4 className="mb-3 mt-4">Authors</h4>
                {formData.authors.map((author, index) => (
                  <div key={index} className="card mb-3">
                    <div className="card-body">
                      <div className="d-flex justify-content-between mb-3">
                        <h5 className="card-title mb-0">Author {index + 1}</h5>
                        {formData.authors.length > 1 && (
                          <button 
                            type="button" 
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeAuthor(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label htmlFor={`author-name-${index}`} className="form-label">Name *</label>
                          <input
                            type="text"
                            className={`form-control ${errors.authors?.[index]?.name ? 'is-invalid' : ''}`}
                            id={`author-name-${index}`}
                            value={author.name}
                            onChange={(e) => handleAuthorChange(index, 'name', e.target.value)}
                          />
                          {errors.authors?.[index]?.name && (
                            <div className="invalid-feedback">{errors.authors[index].name}</div>
                          )}
                        </div>
                        
                        <div className="col-md-6">
                          <label htmlFor={`author-email-${index}`} className="form-label">Email *</label>
                          <input
                            type="email"
                            className={`form-control ${errors.authors?.[index]?.email ? 'is-invalid' : ''}`}
                            id={`author-email-${index}`}
                            value={author.email}
                            onChange={(e) => handleAuthorChange(index, 'email', e.target.value)}
                          />
                          {errors.authors?.[index]?.email && (
                            <div className="invalid-feedback">{errors.authors[index].email}</div>
                          )}
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <label htmlFor={`author-affiliation-${index}`} className="form-label">
                          Affiliation *
                        </label>
                        <input
                          type="text"
                          className={`form-control ${errors.authors?.[index]?.affiliation ? 'is-invalid' : ''}`}
                          id={`author-affiliation-${index}`}
                          value={author.affiliation}
                          onChange={(e) => handleAuthorChange(index, 'affiliation', e.target.value)}
                          placeholder="Institution/Organization"
                        />
                        {errors.authors?.[index]?.affiliation && (
                          <div className="invalid-feedback">{errors.authors[index].affiliation}</div>
                        )}
                      </div>
                      
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="primaryContact"
                          id={`primary-contact-${index}`}
                          checked={author.isPrimaryContact}
                          onChange={() => setPrimaryContact(index)}
                        />
                        <label className="form-check-label" htmlFor={`primary-contact-${index}`}>
                          Primary contact for editorial correspondence
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
                
                {errors.primaryContact && (
                  <div className="alert alert-danger mb-3">{errors.primaryContact}</div>
                )}
                
                <div className="mb-4">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={addAuthor}
                  >
                    <i className="fas fa-plus me-2"></i> Add Another Author
                  </button>
                </div>
                
                {/* Manuscript Upload */}
                <h4 className="mb-3 mt-4">Manuscript File</h4>
                <div className="mb-4">
                  <label htmlFor="manuscriptFile" className="form-label">Upload Manuscript *</label>
                  <input
                    type="file"
                    className={`form-control ${errors.manuscriptFile ? 'is-invalid' : ''}`}
                    id="manuscriptFile"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx"
                  />
                  {errors.manuscriptFile && (
                    <div className="invalid-feedback">{errors.manuscriptFile}</div>
                  )}
                  <div className="form-text">
                    Accepted formats: PDF, DOC, DOCX. Maximum file size: 10MB.
                  </div>
                  {formData.manuscriptFile && (
                    <div className="mt-2">
                      <span className="badge bg-success">
                        <i className="fas fa-check me-1"></i> 
                        File selected: {formData.manuscriptFile.name}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Additional Information */}
                <h4 className="mb-3 mt-4">Additional Information</h4>
                
                <div className="mb-3">
                  <label htmlFor="coverLetter" className="form-label">Cover Letter *</label>
                  <textarea
                    className={`form-control ${errors.coverLetter ? 'is-invalid' : ''}`}
                    id="coverLetter"
                    name="coverLetter"
                    rows="4"
                    value={formData.coverLetter}
                    onChange={handleChange}
                  ></textarea>
                  {errors.coverLetter && (
                    <div className="invalid-feedback">{errors.coverLetter}</div>
                  )}
                  <div className="form-text">
                    Briefly explain why your paper is suitable for this journal and its significance to the field.
                  </div>
                </div>
                
                <div className="mb-3">
                  <label htmlFor="fundingInfo" className="form-label">Funding Information</label>
                  <textarea
                    className="form-control"
                    id="fundingInfo"
                    name="fundingInfo"
                    rows="2"
                    value={formData.fundingInfo}
                    onChange={handleChange}
                    placeholder="If applicable, list funding sources and grant numbers"
                  ></textarea>
                </div>
                
                <div className="mb-3">
                  <label htmlFor="ethicsStatement" className="form-label">
                    Ethics Statement {categoriesRequiringEthics.includes(formData.category) && '*'}
                  </label>
                  <textarea
                    className={`form-control ${errors.ethicsStatement ? 'is-invalid' : ''}`}
                    id="ethicsStatement"
                    name="ethicsStatement"
                    rows="2"
                    value={formData.ethicsStatement}
                    onChange={handleChange}
                    placeholder="Include relevant ethics committee approvals and protocols followed"
                  ></textarea>
                  {errors.ethicsStatement && (
                    <div className="invalid-feedback">{errors.ethicsStatement}</div>
                  )}
                </div>
                
                <div className="mb-3">
                  <label htmlFor="conflictOfInterest" className="form-label">Conflict of Interest</label>
                  <textarea
                    className="form-control"
                    id="conflictOfInterest"
                    name="conflictOfInterest"
                    rows="2"
                    value={formData.conflictOfInterest}
                    onChange={handleChange}
                    placeholder="Declare any potential conflicts of interest"
                  ></textarea>
                </div>
                
                <div className="mb-4">
                  <label htmlFor="acknowledgments" className="form-label">Acknowledgments</label>
                  <textarea
                    className="form-control"
                    id="acknowledgments"
                    name="acknowledgments"
                    rows="2"
                    value={formData.acknowledgments}
                    onChange={handleChange}
                    placeholder="Acknowledge individuals who contributed but do not qualify as authors"
                  ></textarea>
                </div>
                
                {/* Submit Button */}
                <div className="d-grid gap-2">
                  <button 
                    type="submit" 
                    className="btn btn-primary py-2"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Submitting...
                      </>
                    ) : (
                      isRevision ? 'Submit Revision' : 'Submit Paper'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmitPaper;
